import { CheckCircleOutline, Clear, ContentCopy, Done, FileCopyOutlined } from "@mui/icons-material";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography
} from "@mui/material";
import clsx from "clsx";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { t } from "ttag";
import HintTooltip from "../../../components/Tooltip/HintTooltip";
import useStyles from "./SiteSetupResultDialog.style";

// The only place the customer admin user's password is ever shown
const SiteSetupResultDialog = ({ result, onClose }: any) => {
  const classes: any = useStyles();
  const history = useHistory();
  const [copied, setCopied] = useState("");

  if (!result) {
    return null;
  }
  const { site, customer, customerAdminUser } = result;

  const clearCopied = (key: any) => {
    setCopied((current: any) => (current === key ? "" : current));
  };

  const copy = (key: any, value: any) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(key);
      setTimeout(clearCopied, 1500, key);
    });
  };

  const handleCopy = (key: any, value: any) => () => {
    copy(key, value);
  };

  const handleCopyBoth = () => {
    copy("both", `${t`Username`}: ${customerAdminUser.username}\n${t`Password`}: ${customerAdminUser.password}`);
  };

  const handleOpenSite = () => {
    onClose();
    history.push(`/site/${site.id}`);
  };

  const copyButton = (key: any, onCopy: any, tooltip: any, icon: any = <ContentCopy fontSize="small" />) => (
    <HintTooltip title={copied === key ? t`Copied` : tooltip}>
      <IconButton size="small" onClick={onCopy}>
        {copied === key ? <Done fontSize="small" className={classes.copiedIcon} /> : icon}
      </IconButton>
    </HintTooltip>
  );

  const row = (label: any, value: any, extra: any = null) => (
    <div className={classes.row}>
      <Typography variant="body2" className={classes.label}>{label}</Typography>
      {value}
      {extra}
    </div>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth={false}
      classes={{ paper: classes.dialogPaper }}
      aria-labelledby="site-setup-result-title"
    >
      <div className={classes.titleBar}>
        <div className={classes.title}>
          <span className={classes.successBadge}>
            <CheckCircleOutline className={classes.successIcon} />
          </span>
          <Typography variant="h6" id="site-setup-result-title">{t`Site created`}</Typography>
        </div>
        <IconButton onClick={onClose}>
          <Clear />
        </IconButton>
      </div>
      <Typography variant="body2" className={classes.subtitle}>
        {t`${site.name} was added to ${customer.name}`}
      </Typography>

      <DialogContent className={classes.content}>
        <div className={classes.summaryCard}>
          {row(
            t`Customer`,
            <Typography variant="body2" component="div" className={classes.value}>
              {customer.name}
              {customer.isNew && <Chip label={t`New`} size="small" className={classes.newChip} />}
            </Typography>
          )}
          {row(t`Site`, <Typography variant="body2" className={classes.value}>{site.name}</Typography>)}
        </div>

        {customerAdminUser && (
          <div className={classes.credentialsCard}>
            <div className={classes.credentialsHeader}>
              <Typography className={classes.credentialsTitle}>{t`Customer admin user`}</Typography>
              {copyButton(
                "both",
                handleCopyBoth,
                t`Copy username and password`,
                <FileCopyOutlined fontSize="small" />
              )}
            </div>
            <div className={classes.credentialsBody}>
              {row(
                t`Username`,
                <Typography variant="body2" className={classes.value}>{customerAdminUser.username}</Typography>,
                copyButton("username", handleCopy("username", customerAdminUser.username), t`Copy username`)
              )}
              {row(
                t`Password`,
                <Typography variant="body2" className={clsx(classes.value, classes.password)}>
                  {customerAdminUser.password}
                </Typography>,
                copyButton("password", handleCopy("password", customerAdminUser.password), t`Copy password`)
              )}
            </div>
            <Alert severity="warning" className={classes.warning}>
              {t`Save the password now. It is not stored anywhere and won't be shown again.`}
            </Alert>
          </div>
        )}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button variant="outlined" className={classes.actionButton} onClick={handleOpenSite}>{t`Open site`}</Button>
        <Button variant="contained" className={classes.actionButton} onClick={onClose}>{t`Done`}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SiteSetupResultDialog;
